import { useMemo } from "react";
import { formatDistanceToNow } from "date-fns";
import { ArrowLeft, ExternalLink, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { computeMetrics, filterByWindow, WINDOWS } from "@/lib/affiliateMetrics";
import { useState } from "react";

type Affiliate = {
  id?: string;
  slug?: string;
  name: string;
  tagline?: string | null;
  description?: string | null;
  logo_url?: string | null;
  website?: string | null;
  contact_email?: string | null;
  location?: string | null;
  category: string;
  services?: string[] | null;
  specialties?: string[] | null;
  status: string;
  featured: boolean;
  owner_user_id?: string | null;
};

type Lead = {
  id: string;
  affiliate_id: string;
  name: string;
  email: string;
  organization: string | null;
  project_summary: string;
  budget_range: string | null;
  timeline: string | null;
  status: string;
  notes?: string | null;
  created_at: string;
  updated_at?: string | null;
};

interface Props {
  affiliate: Affiliate;
  leads: Lead[];
  onBack: () => void;
  onEdit: () => void;
  onUpdate: (patch: Partial<Affiliate>) => Promise<void>;
  onLeadStatus: (id: string, status: string) => Promise<void>;
}

const fmtPct = (n: number) => `${n.toFixed(0)}%`;
const fmtHours = (h: number | null) =>
  h == null ? "—" : h < 1 ? `${(h * 60).toFixed(0)} min` : `${h.toFixed(1)} h`;

const AffiliateAdminDetail = ({
  affiliate,
  leads,
  onBack,
  onEdit,
  onUpdate,
  onLeadStatus,
}: Props) => {
  const [windowDays, setWindowDays] = useState<number | null>(30);

  const ownLeads = useMemo(
    () => leads.filter((l) => l.affiliate_id === affiliate.id),
    [leads, affiliate.id],
  );

  const metrics = useMemo(
    () => computeMetrics(filterByWindow(ownLeads, windowDays)),
    [ownLeads, windowDays],
  );

  const dashboardUrl = `${window.location.origin}/affiliate/dashboard`;
  const publicUrl = `${window.location.origin}/affiliates/${affiliate.slug ?? ""}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to providers
        </Button>
        <div className="flex items-center gap-2">
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-primary hover:underline inline-flex items-center gap-1"
          >
            Public profile <ExternalLink className="h-3 w-3" />
          </a>
          <Button variant="outline" size="sm" onClick={onEdit} className="gap-2">
            <Pencil className="h-4 w-4" /> Edit
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-start gap-4">
          {affiliate.logo_url ? (
            <img
              src={affiliate.logo_url}
              alt=""
              className="h-14 w-14 rounded-lg object-cover bg-muted"
            />
          ) : (
            <div className="h-14 w-14 rounded-lg bg-muted" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge variant={affiliate.status === "active" ? "default" : "secondary"} className="capitalize">
                {affiliate.status}
              </Badge>
              <Badge variant="outline" className="capitalize">{affiliate.category.replace("_", " ")}</Badge>
              {affiliate.featured ? <Badge>Featured</Badge> : null}
              {affiliate.owner_user_id ? (
                <Badge variant="outline">Linked account</Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">No linked account</Badge>
              )}
            </div>
            <h3 className="font-display text-xl font-bold truncate">{affiliate.name}</h3>
            {affiliate.tagline ? (
              <p className="text-sm text-muted-foreground">{affiliate.tagline}</p>
            ) : null}
            <p className="text-xs text-muted-foreground mt-1">
              {affiliate.contact_email ?? "—"}{affiliate.location ? ` · ${affiliate.location}` : ""}
              {affiliate.website ? (
                <>
                  {" · "}
                  <a href={affiliate.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                    Website
                  </a>
                </>
              ) : null}
            </p>
          </div>

          <div className="flex flex-col gap-2 shrink-0">
            <div className="flex items-center gap-2 text-xs">
              <Switch
                checked={affiliate.status === "active"}
                onCheckedChange={(v) => onUpdate({ status: v ? "active" : "paused" })}
              />
              <span>Active</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Switch
                checked={!!affiliate.featured}
                onCheckedChange={(v) => onUpdate({ featured: v })}
              />
              <span>Featured</span>
            </div>
          </div>
        </div>

        {affiliate.description ? (
          <p className="text-sm text-foreground/80 whitespace-pre-line mt-4">{affiliate.description}</p>
        ) : null}

        {(affiliate.services?.length || affiliate.specialties?.length) ? (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {(affiliate.services ?? []).map((s) => (
              <span key={`s-${s}`} className="text-[11px] bg-primary/10 text-primary px-2 py-0.5 rounded-full">{s}</span>
            ))}
            {(affiliate.specialties ?? []).map((s) => (
              <span key={`sp-${s}`} className="text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{s}</span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Effectiveness
        </h4>
        <div className="flex gap-1.5">
          {WINDOWS.map((w) => (
            <Button
              key={w.label}
              size="sm"
              variant={windowDays === w.days ? "default" : "outline"}
              onClick={() => setWindowDays(w.days)}
            >
              {w.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: "Leads", value: metrics.total },
          { label: "New", value: metrics.newCount },
          { label: "Contacted", value: metrics.contacted },
          { label: "Closed", value: metrics.closed },
          { label: "Conversion", value: fmtPct(metrics.conversionPct) },
          { label: "Response", value: fmtPct(metrics.responsePct) },
          { label: "Avg reply", value: fmtHours(metrics.avgResponseHours) },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-border bg-card p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</div>
            <div className="text-lg font-display font-bold mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Lead activity
          </h4>
          <p className="text-xs text-muted-foreground">
            Dashboard: <span className="font-mono">{dashboardUrl}</span>
          </p>
        </div>

        {ownLeads.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">
            No leads yet.
          </p>
        ) : (
          <div className="space-y-2">
            {ownLeads.map((l) => (
              <div key={l.id} className="bg-card border border-border rounded-lg p-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                      <Badge variant="outline" className="capitalize">{l.status}</Badge>
                      <span>{formatDistanceToNow(new Date(l.created_at), { addSuffix: true })}</span>
                      <span>· {l.email}</span>
                      {l.organization ? <span>· {l.organization}</span> : null}
                    </div>
                    <p className="text-sm font-semibold mt-1">{l.name}</p>
                    <p className="text-sm text-foreground/80 mt-1 whitespace-pre-line">{l.project_summary}</p>
                    {l.notes ? (
                      <p className="text-xs text-muted-foreground mt-2 border-l-2 border-primary/50 pl-2 italic">
                        Affiliate notes: {l.notes}
                      </p>
                    ) : null}
                  </div>
                  <Select value={l.status} onValueChange={(v) => onLeadStatus(l.id, v)}>
                    <SelectTrigger className="w-32 shrink-0"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">new</SelectItem>
                      <SelectItem value="contacted">contacted</SelectItem>
                      <SelectItem value="closed">closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateAdminDetail;